/**
 * AIShield useRequestDetail Hook
 * Manages request inspection detail fetching with independent loading/error states,
 * cancellation of obsolete requests, and sequence counter protection against race conditions.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import type { RequestRecord } from "../types/telemetry.ts";
import { fetchRequestDetail } from "../services/apiClient.ts";

export interface UseRequestDetailResult {
  requestId: string | null;
  request: RequestRecord | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useRequestDetail(
  requestId: string | null,
  websiteId?: string | null,
  isDemo?: boolean
): UseRequestDetailResult {
  const [detailState, setDetailState] = useState<{
    requestId: string | null;
    request: RequestRecord | null;
    isLoading: boolean;
    error: Error | null;
  }>({
    requestId: null,
    request: null,
    isLoading: false,
    error: null,
  });

  const activeSeqRef = useRef<number>(0);
  const activeControllerRef = useRef<AbortController | null>(null);
  const currentIdRef = useRef<string | null>(requestId);
  currentIdRef.current = requestId;
  const currentWebsiteIdRef = useRef<string | null | undefined>(websiteId);
  currentWebsiteIdRef.current = websiteId;

  const loadDetail = useCallback(
    async (id: string, seq: number) => {
      // Abort previous in-flight request
      if (activeControllerRef.current) {
        activeControllerRef.current.abort();
      }
      const controller = new AbortController();
      activeControllerRef.current = controller;

      try {
        const data = await fetchRequestDetail(id, {
          signal: controller.signal,
          websiteId: currentWebsiteIdRef.current || undefined,
          isDemo,
        });
        if (seq === activeSeqRef.current) {
          setDetailState({
            requestId: id,
            request: data,
            isLoading: false,
            error: null,
          });
        }
      } catch (err: unknown) {
      if (seq === activeSeqRef.current) {
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }
        setDetailState({
          requestId: id,
          request: null,
          isLoading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      }
    } finally {
      if (seq === activeSeqRef.current) {
        activeControllerRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    const seq = ++activeSeqRef.current;
    if (activeControllerRef.current) {
      activeControllerRef.current.abort();
      activeControllerRef.current = null;
    }

    if (!requestId) {
      // Drawer closed: immediately stop exposing previous request details
      setDetailState({
        requestId: null,
        request: null,
        isLoading: false,
        error: null,
      });
      return;
    }

    // Selection changed: immediately stop exposing previous request details
    setDetailState({
      requestId,
      request: null,
      isLoading: true,
      error: null,
    });

    loadDetail(requestId, seq);

    return () => {
      // Invalidate obsolete responses on selection changes and unmount
      activeSeqRef.current++;
      if (activeControllerRef.current) {
        activeControllerRef.current.abort();
        activeControllerRef.current = null;
      }
    };
  }, [requestId, websiteId, isDemo, loadDetail]);

  const refetch = useCallback(async () => {
    if (currentIdRef.current) {
      const seq = ++activeSeqRef.current;
      setDetailState((prev) => ({ ...prev, isLoading: true, error: null }));
      await loadDetail(currentIdRef.current, seq);
    }
  }, [loadDetail]);

  const isClosed = !requestId;
  const isStale = detailState.requestId !== requestId;

  return {
    requestId,
    request: isClosed || isStale ? null : detailState.request,
    isLoading: isClosed ? false : isStale ? true : detailState.isLoading,
    error: isClosed || isStale ? null : detailState.error,
    refetch,
  };
}

